<template>
    <!-- opens the caller's customer when the signed-in user picks up a Rinkel call, on any page -->
    <li v-if="can('CanViewCall')" class="flex items-center gap-2" v-tooltip.bottom="tooltip">
        <i class="pi pi-phone" :class="state === 'connected' ? 'text-primary' : 'text-surface-500'" aria-hidden="true" />
        <ToggleSwitch v-model="follow" aria-label="Open the customer when I answer a call" />
    </li>
</template>

<script setup lang="ts">
import { CALL_ANSWERED, type CallAnsweredNotification } from '@/api/notifications';
import { useCurrentUser } from '@/composables/useCurrentUser';
import { useNotificationHub } from '@/composables/useNotificationHub';
import { useStorage } from '@vueuse/core';
import { computed, onScopeDispose, watch } from 'vue';
import { useRouter } from 'vue-router';

const router = useRouter();
const { can } = useCurrentUser();
const hub = useNotificationHub();

//per browser, as the user leaves it
const follow = useStorage('calls.followAnswered', false);

//what the icon shows: off while the toggle is off, otherwise the shared connection's state
const state = computed(() => (follow.value ? hub.state.value : 'off'));
const tooltip = computed(
    () =>
        ({
            off: 'Open the customer when I answer a call',
            disconnected: 'Not connected: answered calls do not open yet',
            connecting: 'Not connected: answered calls do not open yet',
            connected: 'Following: the customer opens when I answer a call',
            refused: 'Cannot follow calls: your account has no e-mail address'
        })[state.value]
);

//a retried Rinkel delivery pushes the same call again
const handled = new Set<string>();

async function open(call: CallAnsweredNotification) {
    //the Calls page may hold the connection while the follow is off
    if (!follow.value || handled.has(call.callId)) return;
    handled.add(call.callId);
    if (call.customerId) await router.push({ name: 'customers-view', params: { customerId: call.customerId } });
    else if (call.phoneNumber) await router.push({ name: 'customers-all', query: { criteria: call.phoneNumber } });
}

const off = hub.on(CALL_ANSWERED, (call) => void open(call as CallAnsweredNotification));
let release: (() => void) | undefined;

//can() is false until /me has loaded, so the connection waits for it
watch(
    () => follow.value && can('CanViewCall'),
    (on) => {
        release?.();
        release = on ? hub.acquire() : undefined;
    },
    { immediate: true }
);
onScopeDispose(() => {
    off();
    release?.();
});
</script>
